import { useEffect, useState, useMemo } from 'react';

import { AnyAction } from 'redux';
import { useSelector, useDispatch } from 'react-redux';

import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { RootState } from 'store/types';
import * as selectors from 'store/amwell/selectors';
import * as actions from 'store/amwell/actions';
import {
  Appointment,
  Consumer,
  State,
  Provider,
  VisitContext,
  ProviderVisibility,
  UpdateVisitContextForm,
  OnDemandSpecialty,
  Practice
} from 'store/amwell/types';

dayjs.extend(relativeTime);

export interface NavigatorState {
  patients: Consumer[];
  providers: Provider[];
  appointments: Appointment[];
  usStates: State[];
  provider: Provider | null;
  location: State | null;
  activeConsumer: Consumer | null;
  appointment: Appointment | null;
  visitContext: VisitContext | null;
  specialty: OnDemandSpecialty | null;
  consumer: Consumer | null;
  loading: boolean;
}

export interface NavigatorMethods {
  consumer: (newConsumer: Consumer) => Promise<void>;
  provider: (newProvider: Provider) => Promise<void>;
  location: (newLocation: State) => Promise<void>;
  visitContext: {
    new: (form: Partial<UpdateVisitContextForm>) => Promise<void>;
    appointment: (
      newAppointment: Appointment,
      form: Partial<UpdateVisitContextForm>
    ) => Promise<void>;
  };
}

const firstAvailable = ({
  sourceId: 'firstAvailable',
  fullName: 'First Available',
  practice: {
    name: ''
  },
  hidden: true
} as unknown) as Provider;

const catchError = (res?: AnyAction) => {
  if (res?.error) {
    throw res.error;
  }
};

export function useConnectCareNavigator(): [NavigatorState, NavigatorMethods] {
  const dispatch = useDispatch();
  const usStates = useSelector(selectors.usStatesSelector);
  const consumer = useSelector(selectors.consumerDataSelector);
  const activeConsumer = useSelector(selectors.activeConsumerDataSelector);
  const dependents = useSelector(selectors.dependentsDataSelector);
  const appointments = useSelector(selectors.appointmentsDataSelector);
  const appointment = useSelector(selectors.appointmentDataSelector);
  const practices = useSelector(selectors.practicesListDataSelector);
  const providerDetails = useSelector(selectors.providerDetailsDataSelector);
  const specialty = useSelector(selectors.specialtyDataSelector);
  const visitContext = useSelector(selectors.visitContextDataSelector);
  const loading = useSelector(
    (state: RootState) =>
      selectors.appointmentsLoadingSelector(state) ||
      selectors.dependentsDataLoadingSelector(state) ||
      selectors.practicesListDataLoadingSelector(state) ||
      selectors.providersListDataLoadingSelector(state)
  );

  const [availableProviders, setAvailableProviders] = useState<Provider[]>([]);

  const getAllProviders = async () => {
    const availableMap = [ProviderVisibility.WEB_AVAILABLE, ProviderVisibility.WEB_BUSY];

    const resPractices: AnyAction = await dispatch(actions.getPractices());

    if (!Array.isArray(resPractices?.payload?.practices) || resPractices?.error) return;

    const resProviders: Provider[] = await Promise.all(
      resPractices.payload.practices.map(async (practice: Practice) => {
        const provRes: AnyAction = await dispatch(actions.getProvidersForPractice({ practice }));
        if (provRes?.error || !provRes?.payload) return [];

        return provRes.payload.filter((payload: Provider) =>
          availableMap.includes(payload.visibility)
        );
      })
    );

    const allProviders = uniqBy(flatten(resProviders), p => p.sourceId);
    setAvailableProviders(specialty ? [firstAvailable, ...allProviders] : allProviders);
  };

  useEffect(() => {
    async function init() {
      if (consumer && !activeConsumer) {
        await dispatch(actions.updateConsumer({ consumer }));
      }
      getAllProviders();
      dispatch(actions.getCountries());
      dispatch(actions.getAppointments());
      dispatch(actions.getDependents());
    }

    init();
  }, []);

  const availableAppointments = useMemo(() => {
    return appointments.filter(appt => {
      const difference = dayjs(appt.schedule.scheduledStartTime).diff(dayjs(), 'minute');
      return difference <= 30 && difference >= -30;
    });
  }, [appointments]);

  const availablePatients = useMemo(() => {
    return consumer ? [consumer, ...dependents] : [];
  }, [consumer, dependents]);

  const provider = useMemo(() => {
    const id = specialty ? 'firstAvailable' : providerDetails?.sourceId;
    const res = availableProviders.find(p => p.sourceId === id);
    return res || null;
  }, [specialty, availableProviders, providerDetails]);

  const location = useMemo(() => {
    if (!consumer) return null;
    const stateCode = consumer.legalResidence?.code || 'UT';
    const usState = usStates.find(state => state.code === stateCode);
    return usState || null;
  }, [consumer, usStates]);

  const state: NavigatorState = {
    patients: availablePatients,
    providers: availableProviders,
    appointments: availableAppointments,
    usStates,
    provider,
    location,
    activeConsumer,
    appointment,
    visitContext,
    specialty,
    consumer,
    loading
  };

  const set: NavigatorMethods = {
    consumer: async (newConsumer: Consumer) => {
      if (!consumer) return;

      const res: AnyAction = await dispatch(actions.updateConsumer({ consumer: newConsumer }));
      catchError(res);
    },
    provider: async (newProvider: Provider) => {
      let res: AnyAction;

      const practice = newProvider.practice.sourceId
        ? newProvider.practice
        : practices.find(p => p.id?.persistentId === newProvider.practice.id?.persistentId);

      if (!practice) return;

      res = await dispatch(actions.getProvidersForPractice({ practice }));
      catchError(res);

      res = await dispatch(actions.getProviderDetail({ provider: newProvider }));
      catchError(res);
    },
    location: async (newLocation: State) => {
      if (!consumer) return;

      let res: AnyAction;

      if (activeConsumer?.isDependent) {
        res = await dispatch(actions.updateConsumer({ consumer }));
        catchError(res);
      }

      res = await dispatch(actions.updateConsumerLegalResidence({ stateCode: newLocation.code }));
      catchError(res);

      getAllProviders();
    },
    visitContext: {
      new: async (form: Partial<UpdateVisitContextForm>) => {
        let res: AnyAction;

        res = await dispatch(actions.getVisitContext());
        catchError({ ...res, error: !res.payload?.visitContext ? new Error() : res?.error });

        res = await dispatch(
          actions.updateVisitContext({
            updateVisitContextForm: { ...res.payload.visitContext, ...form }
          })
        );
      },
      appointment: async (newAppointment: Appointment, form: Partial<UpdateVisitContextForm>) => {
        let res: AnyAction;

        res = await dispatch(actions.getAppointment({ appointment: newAppointment }));
        catchError(res);

        res = await dispatch(
          actions.createVisitContextWithAppointment({
            appointment: newAppointment
          })
        );
        catchError({ ...res, error: !res.payload?.visitContext ? new Error() : res?.error });

        res = await dispatch(actions.updateVisitContext({ updateVisitContextForm: form }));
      }
    }
  };

  return [state, set];
}
